import {
  Building2,
  Target,
  Award,
  CheckCircle,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import React from "react";
import AboutHero from "@/components/about/AboutHero";
import Navbar from "@/components/Navbar"; // Import Navbar
import Footer from "@/components/Footer"; // Import Footer

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar /> {/* Navigation */}
      <AboutHero />
      <main className="relative">
        {/* Company Overview Section */}
        <section className="py-20 bg-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-16">
              <div className="inline-block">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Commercial Property{" "}
                  <span className="text-[#2596be] relative">
                    LK
                    <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-[#2596be] to-[#0097b3] rounded-full"></div>
                  </span>
                </h2>
              </div>
              <div className="w-24 h-1 bg-gradient-to-r from-[#2596be] to-[#0097b3] mx-auto mb-8 rounded-full"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Your premier commercial real estate brokerage firm, expertly
                serving businesses across Sri Lanka since 2024
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="prose prose-lg text-gray-700">
                  <p className="text-lg leading-relaxed">
                    Commercialproperty.lk is a{" "}
                    <strong className="text-[#2596be]">
                      premier commercial real estate brokerage firm
                    </strong>{" "}
                    based in Colombo, Sri Lanka. Founded in 2024, we specialize
                    in assisting businesses with all their commercial and
                    industrial property needs, including lease agreements and
                    direct sales transactions.
                  </p>
                  <p className="text-lg leading-relaxed">
                    Our team of experienced brokers helps clients{" "}
                    <strong className="text-[#2596be]">
                      save costs & time
                    </strong>{" "}
                    by locating the best properties for their requirements. We
                    free up companies from the time-consuming research required
                    to find the ideal space, making the process more efficient
                    and informed.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-8 rounded-2xl border border-blue-200/50 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#2596be] rounded-full flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-blue-900">
                      Our Mission
                    </h3>
                  </div>
                  <p className="text-blue-800 text-lg leading-relaxed">
                    To be the trusted partner for businesses looking to expand
                    or optimize their commercial real estate footprint in Sri
                    Lanka.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-[#2596be] to-[#0097b3] rounded-3xl p-8 text-white shadow-2xl transform hover:scale-105 transition-all duration-300">
                  <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                    <Award className="w-8 h-8" />
                    Why Choose Us?
                  </h3>
                  <div className="space-y-6">
                    {[
                      "Expert local market knowledge",
                      "Personalized service approach",
                      "Comprehensive property solutions",
                      "Proven track record of success",
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 group"
                      >
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-lg">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Floating decoration */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-[#2596be]/20 to-[#0097b3]/20 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#2596be] to-[#0097b3] relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-32 -translate-y-32 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full translate-x-32 translate-y-32 blur-3xl"></div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Find Your Perfect Commercial Space?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              Contact us today to learn how we can help your company succeed in
              the Sri Lankan commercial real estate market.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-[#2596be] px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/properties"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-[#2596be] transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                View Properties
                <Building2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </a>
            </div>

            {/* Contact info */}
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-blue-100">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span>+94 11 123 4567</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span>info@commercialproperty.lk</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer /> {/* Footer */}
    </div>
  );
};

export default AboutPage;
